// @ts-check
/**
 * Cart Channel Attribute + DY Consent Attributes
 *
 * Ensures the Shopify cart carries channel: "web" and, when DY cookies exist,
 * a `dy` JSON attribute. Does not fetch /cart.js — Liquid / _CURRENT_CART is
 * the seed. POSTs only when something is actually missing or DY ids changed.
 */
(function CartChannelAttribute() {
  'use strict';

  if (window.__cartChannelAttributeInit) return;
  window.__cartChannelAttributeInit = true;

  var CHANNEL_KEY = 'channel';
  var CHANNEL_VALUE = 'web';
  var DY_POLL_INTERVAL = 200;
  var DY_POLL_TIMEOUT = 3000;
  var seed = window.__CART_CHANNEL_SEED__ || {};

  /**
   * @param {string} name
   * @returns {string}
   */
  function getCookie(name) {
    var escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  /**
   * @returns {boolean}
   */
  function hasConsentAccepted() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp) {
      if (typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed()) return true;
      if (typeof cp.marketingAllowed === 'function' && cp.marketingAllowed()) return true;
    }
    var cy = getCookie('cookieyes-consent');
    if (cy && cy.indexOf('accepted:yes') !== -1) return true;
    if (getCookie('OptanonAlertBoxClosed') !== '') return true;
    if (getCookie('cookiesAccepted') === 'true') return true;
    return false;
  }

  /**
   * @returns {{ dyid: string, dyid_server: string, session: string } | null}
   */
  function readDyCookies() {
    var dyid = getCookie('_dyid');
    if (dyid === '') return null;
    var dySession = getCookie('_dyjsession');
    if (!dySession && window.DY && window.DY.jsession) dySession = window.DY.jsession;
    return {
      dyid: dyid,
      dyid_server: getCookie('_dyid_server'),
      session: dySession || '',
    };
  }

  /**
   * @returns {Promise<{ dyid: string, dyid_server: string, session: string } | null>}
   */
  function waitForDYCookies() {
    var immediate = readDyCookies();
    if (immediate) return Promise.resolve(immediate);

    return new Promise(function (resolve) {
      var elapsed = 0;
      var interval = setInterval(function () {
        var cookies = readDyCookies();
        if (cookies) {
          clearInterval(interval);
          resolve(cookies);
          return;
        }
        elapsed += DY_POLL_INTERVAL;
        if (elapsed >= DY_POLL_TIMEOUT) {
          clearInterval(interval);
          resolve(null);
        }
      }, DY_POLL_INTERVAL);
    });
  }

  /**
   * @returns {Record<string, unknown>}
   */
  function seedAttributes() {
    var fromCart = window._CURRENT_CART && window._CURRENT_CART.cart && window._CURRENT_CART.cart.attributes;
    if (fromCart) return fromCart;
    return {
      channel: seed.channel || '',
      dy: seed.dy || '',
    };
  }

  /**
   * @param {{ dyid: string, dyid_server: string, session: string } | null} dyCookies
   * @returns {Record<string, string>}
   */
  function buildDesiredAttributes(dyCookies) {
    var attrs = {};
    attrs[CHANNEL_KEY] = CHANNEL_VALUE;
    if (dyCookies !== null) {
      attrs.dy = JSON.stringify({
        active_consent_accepted: hasConsentAccepted(),
        dyid: dyCookies.dyid,
        dyid_server: dyCookies.dyid_server,
        session: dyCookies.session,
      });
    }
    return attrs;
  }

  /**
   * Write `dy` only when ids/session change, or consent upgrades false → true.
   * Never flap true → false (banner APIs are often unread on first paint).
   *
   * @param {string} desiredDy
   * @param {string} existingDy
   * @returns {boolean}
   */
  function shouldWriteDy(desiredDy, existingDy) {
    if (!desiredDy) return false;
    if (!existingDy) return true;
    try {
      var next = JSON.parse(desiredDy);
      var prev = JSON.parse(existingDy);
      if (String(prev.dyid || '') !== String(next.dyid || '')) return true;
      if (String(prev.dyid_server || '') !== String(next.dyid_server || '')) return true;
      if (String(prev.session || '') !== String(next.session || '')) return true;
      if (prev.active_consent_accepted !== true && next.active_consent_accepted === true) return true;
      return false;
    } catch (e) {
      return existingDy !== desiredDy;
    }
  }

  /**
   * @param {Record<string, string>} desired
   * @param {Record<string, unknown>} existing
   * @returns {Record<string, string>}
   */
  function diffAttributes(desired, existing) {
    var diff = {};
    var currentChannel = existing[CHANNEL_KEY] !== undefined ? String(existing[CHANNEL_KEY]).trim() : '';
    if (desired[CHANNEL_KEY] && currentChannel !== desired[CHANNEL_KEY]) {
      diff[CHANNEL_KEY] = desired[CHANNEL_KEY];
    }
    var currentDy = existing.dy !== undefined ? String(existing.dy) : '';
    if (desired.dy && shouldWriteDy(desired.dy, currentDy)) {
      diff.dy = desired.dy;
    }
    return diff;
  }

  /**
   * @param {Record<string, string>} attributes
   * @returns {Promise<void>}
   */
  function applyAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).catch(function () {});
  }

  /**
   * @param {{ dyid: string, dyid_server: string, session: string } | null} dyCookies
   * @param {Record<string, unknown>} existing
   * @returns {Promise<void> | void}
   */
  function writeFrom(dyCookies, existing) {
    var desired = buildDesiredAttributes(dyCookies);
    var diff = diffAttributes(desired, existing);
    if (Object.keys(diff).length === 0) return;
    return applyAttributes(diff);
  }

  function run() {
    var existing = seedAttributes();
    var channelOk = String(existing[CHANNEL_KEY] || '').trim() === CHANNEL_VALUE;
    var cookiesNow = readDyCookies();

    if (cookiesNow) {
      return writeFrom(cookiesNow, existing);
    }

    if (!channelOk) {
      writeFrom(null, existing);
    }

    if (seed.dyEnabled === false) return;

    return waitForDYCookies().then(function (dyCookies) {
      if (!dyCookies) return;
      return writeFrom(dyCookies, seedAttributes());
    });
  }

  function start() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function () { run(); }, { timeout: 4000 });
    } else {
      setTimeout(run, 1);
    }
  }

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
